import { Component, DestroyRef, HostListener, inject, signal, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TranslocoDirective } from '@jsverse/transloco';
import dayjs, { Dayjs } from 'dayjs';
import { DateRange } from '../service/tenant-unit-date-range-filter.service';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { DayjsDateAdapter, MAT_DAYJS_DATE_ADAPTER_OPTIONS, MAT_DAYJS_DATE_FORMATS } from '../../../util/material-dayjs-adapter';
import { Billing, BillingCustomCService, BillingPeriod, BillingPeriodService } from '@eadsysteme/billingclient-v1';
import { MatOption, MatSelect } from '@angular/material/select';
import { SessionService } from '../../../services/session.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DateUtil } from '../../../util/DateUtil';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { CdkDrag, CdkDragHandle } from '@angular/cdk/drag-drop';

@Component({
	selector: 'ara-date-range-filter-dialog',
	templateUrl: './date-range-filter-dialog.component.html',
	imports: [
		MatDialogModule,
		MatFormFieldModule,
		MatInputModule,
		MatDatepickerModule,
		MatButtonModule,
		ReactiveFormsModule,
		TranslocoDirective,
		MatOption,
		MatSelect,
		MatIcon,
		MatTooltip,
		CdkDrag,
		CdkDragHandle,
	],
	changeDetection: ChangeDetectionStrategy.Eager,
	providers: [
		{ provide: DateAdapter, useClass: DayjsDateAdapter, deps: [MAT_DATE_LOCALE, MAT_DAYJS_DATE_ADAPTER_OPTIONS] },
		{ provide: MAT_DATE_FORMATS, useValue: MAT_DAYJS_DATE_FORMATS },
	],
})
export class DateRangeFilterDialogComponent {
	private readonly dialogRef = inject(MatDialogRef<DateRangeFilterDialogComponent>);
	private readonly data = inject<DateRange>(MAT_DIALOG_DATA);
	private readonly destroyRef = inject(DestroyRef);
	private readonly sessionService = inject(SessionService);
	private readonly billingService = inject(BillingCustomCService);
	private readonly billingPeriodService = inject(BillingPeriodService);
	protected readonly dateUtil = inject(DateUtil);

	protected readonly dayjs = dayjs;
	protected readonly YEARS = 'years';
	protected billings = signal<Billing[]>([]);
	protected periods = signal<BillingPeriod[]>([]);

	dateForm = new FormGroup(
		{
			validFrom: new FormControl<Dayjs>(this.data.validFrom),
			validTo: new FormControl<Dayjs>(this.data.validTo),
		},
		{ validators: this.dateUtil.dateRange('validFrom', 'validTo', 'day') }
	);
	billingDateForm = new FormGroup({
		billing: new FormControl<Billing>(null),
		billingPeriod: new FormControl<BillingPeriod>(null),
	});

	constructor() {
		this.billingService
			.getBillingsByRealEstate(this.sessionService.effectiveArea, this.data.realEstate.id)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (billings) => {
					this.billings.set(billings);
					if (billings.length === 1) {
						const billing = billings[0];
						this.billingDateForm.get('billing').setValue(billing);
						this.loadPeriods(billing);
					}
				},
			});
	}

	onCancel(): void {
		this.dialogRef.close();
	}

	@HostListener('document:keydown.escape', ['$event'])
	onEscapeKey(event: Event): void {
		event.preventDefault();
		this.onCancel();
	}

	onSave(): void {
		if (this.dateForm.valid) {
			this.dialogRef.close({
				validFrom: this.dateForm.value.validFrom,
				validTo: this.dateForm.value.validTo,
			} as DateRange);
		}
	}

	get validFrom() {
		return this.dateForm.get('validFrom');
	}

	get validTo() {
		return this.dateForm.get('validTo');
	}

	getPeriodDate() {
		return this.billingDateForm.get('billingPeriod');
	}

	protected loadPeriods(value: Billing) {
		this.billingPeriodService
			.readAllBillingPeriod(this.sessionService.effectiveArea, value.exportId)
			.pipe(takeUntilDestroyed(this.destroyRef))
			.subscribe({
				next: (periods) => {
					this.periods.set(periods);
					if (periods.length === 1) {
						this.billingDateForm.get('billingPeriod').setValue(periods[0]);
					}
				},
			});
	}

	protected copyPeriodDate() {
		this.validFrom.setValue(dayjs(this.getPeriodDate().value.validFrom));
		this.validTo.setValue(dayjs(this.getPeriodDate().value.validTo));
		this.getPeriodDate().setValue(null);
	}
}
